import React, { useState } from "react";
import PropTypes from "prop-types";
import DatePicker, { registerLocale } from "react-datepicker";
import ko from "date-fns/locale/ko";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./E3DateTime.module.css";
import * as dateFns from "date-fns";

registerLocale("ko", ko);

/**
 * E3DateTime
 *  type ['text', 'email', 'password', 'number', 'color', 'search', 'tel'] 를 지원함
 * @type React.FC<E3DateTimePropTypes>
 */
const E3DateTime = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3DateTimePropTypes).includes(p)) {
      console.error(`E3DateTime: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    setValue: (source) => {
      if (typeof source === "string") {
        if (source)
          source = dateFns.parse(source, props.dateFormat, new Date());
      }

      setCurrState((prev) => ({
        ...prev,
        value: source,
      }));
    },
    getValue: () => {
      if (currState.value) {
        return dateFns.format(currState.value, props.dateFormat);
      }
      return "";
    },
    clear: () => {
      setCurrState((prev) => ({
        ...prev,
        value: props.defaultValue ?? null,
      }));
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        readOnly: isReadOnly,
      }));
    },

    setMinDate: (source) => {
      if (typeof source === "string") {
        source = dateFns.parse(source, props.dateFormat, new Date());
      }
      setCurrState((prev) => ({
        ...prev,
        minDate: source,
      }));
    },

    setMaxDate: (source) => {
      if (typeof source === "string") {
        source = dateFns.parse(source, props.dateFormat, new Date());
      }
      setCurrState((prev) => ({
        ...prev,
        maxDate: source,
      }));
    },
    setStartDate: (source) => {
      if (typeof source === "string") {
        source = dateFns.parse(source, props.dateFormat, new Date());
      }
      setCurrState((prev) => ({
        ...prev,
        startDate: source,
      }));
    },
    setEndDate: (source) => {
      if (typeof source === "string") {
        source = dateFns.parse(source, props.dateFormat, new Date());
      }
      setCurrState((prev) => ({
        ...prev,
        endDate: source,
      }));
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "E3Input: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  /**
   * E3DateTime Component의 State
   */
  const [currState, setCurrState] = useState({
    value: props.defaultValue, // Date() 타입
    minDate: props.minDate,
    maxDate: props.maxDate,
    readOnly: props.readOnly,
    disabled: props.disabled,
    startDate: props.startDate,
    endDate: props.endDate,
    showMonthYearPicker: props.showMonthYearPicker,
    showYearPicker: props.showYearPicker,
  });

  const _onChange = (e) => {
    setCurrState((prev) => ({
      ...prev,
      value: e,
    }));

    if (props.onChange) {
      const value = e;
      props.onChange(value);
    }
  };

  return (
    <DatePicker
      className={`${styles.e3DateTime} ${props.controlWidth ?? ""} ${
        props.className ?? ""
      }`}
      locale="ko"
      selected={currState.value ? currState.value : null}
      readOnly={currState.readOnly}
      disabled={currState.disabled}
      {...(currState.minDate && { minDate: currState.minDate })}
      {...(currState.maxDate && { maxDate: currState.maxDate })}
      {...(currState.startDate && { startDate: currState.startDate })}
      {...(currState.endDate && { endDate: currState.endDate })}
      {...(props.timeIntervals && { timeIntervals: props.timeIntervals })}
      dateFormat={props.dateFormat}
      showMonthYearPicker={props.showMonthYearPicker}
      showYearPicker={props.showYearPicker}
      selectsStart={props.selectsStart}
      selectsEnd={props.selectsEnd}
      showTimeSelect={props.showTimeSelect}
      showTimeSelectOnly={props.showTimeSelectOnly}
      isClearable={props.showClearButton}
      onChange={_onChange}
      showYearDropdown={props.showYearDropdown}
      scrollableYearDropdown={props.scrollableYearDropdown}
      showMonthDropdown={props.showMonthDropdown}
      excludeDates={props.excludeDates}
      yearDropdownItemNumber={props.yearDropdownItemNumber}
    />
  );
});

const E3DateTimePropTypes = {
  /** 기본 지정일자. 기본값은 {new Date()}. Date() 오브젝트. Rendering 이후 사용되지 않음 */
  defaultValue: PropTypes.object,

  /** 날짜 포맷: 기본값은 {'yyyy-MM-dd'}. */
  dateFormat: PropTypes.string,

  /** onChange 함수: 기본값은 {null}. */
  onChange: PropTypes.func,

  /** 초기 읽기전용: 기본값은 {false}. 이후 setReadOnly()로 제어 가능 */
  readOnly: PropTypes.bool,

  /** 초기 비활성화: 기본값은 {false}. 이후 enable(), disable()로 제어 가능 */
  disabled: PropTypes.bool,

  /** 초기 선택가능 최소값: 기본값은 {null}. Date() 오브젝트. 이후 setMinDate()로 제어 가능 */
  minDate: PropTypes.object,

  /** 초기 선택가능 최대값: 기본값은 {null}. Date() 오브젝트. 이후 setMaxDate()로 제어 가능 */
  maxDate: PropTypes.object,

  /** Date Range용 시작일자 여부: 기본값은 {false}. */
  selectsStart: PropTypes.bool,

  /** Date Range용 종료일자 여부: 기본값은 {false}. */
  selectsEnd: PropTypes.bool,

  /** Date Range용 초기 시작일자: 기본값은 {null}. 이후 setStartDate()로 제어 가능 */
  startDate: PropTypes.object,

  /** Date Range용 초기 종료일자: 기본값은 {null}. 이후 setEndDate()로 제어 가능 */
  endDate: PropTypes.object,

  /** 시간 선택 활성화: 기본값은 {false}. */
  showTimeSelect: PropTypes.bool,

  /** 시간 선택만 활성화 및 Date 선택 비활성: 기본값은 {false} */
  showTimeSelectOnly: PropTypes.bool,

  /** 시간 선택 시 interval */
  timeIntervals: PropTypes.number,

  /** 초기화 버튼 여부 */
  showClearButton: PropTypes.bool,

  /** 월 단위 달력 활성화 여부 */
  showMonthYearPicker: PropTypes.bool,

  /** 연 단위 달력 활성화 여부 */
  showYearPicker: PropTypes.bool,

  /** 년 단위 달력 dropDown 여부 */
  showYearDropdown: PropTypes.bool,

  /** 년 단위 드롭다운 스크롤 시 보여줄 아이템 수 */
  yearDropdownItemNumber: PropTypes.number,

  /** 년 단위 달력 dropDown scroll 여부 */
  scrollableYearDropdown: PropTypes.bool,

  /** 월 단위 달력 dropDown 여부 */
  showMonthDropdown: PropTypes.bool,

  controlWidth: PropTypes.string,

  excludeDates: PropTypes.array,
};

E3DateTime.propTypes = E3DateTimePropTypes;

E3DateTime.defaultProps = {
  defaultValue: new Date(), // 기본 값. null 지정 시 빈 값
  dateFormat: "yyyy-MM-dd",
  onChange: null,
  readOnly: false,
  disabled: false,
  minDate: null,
  maxDate: null,
  selectsStart: false, // Range를 위한 prop
  selectsEnd: false, // Range를 위한 prop
  startDate: null, // Range를 위한 prop
  endDate: null, // Range를 위한 prop
  showTimeSelect: false, // timepicker 활성화
  showTimeSelectOnly: false, // datepicker 대신 timepicker만 활성화
  timeIntervals: 1,
  showClearButton: false,
  controlWidth: null,
  showYearDropdown: true,
  scrollableYearDropdown: true,
  showMonthDropdown: true,
  excludeDates: null,
  yearDropdownItemNumber: 50,
};
E3DateTime.displayName = "E3DateTime";
export default E3DateTime;
